import React, { Suspense, useMemo } from "react";

import Sidebar from "../Sidebar";
import Header from "../Header";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { filterLinksByPermissions } from "../../config/permissions";
import lazyWithReload from "../../utils/lazyWithReload";
import StudentGoalsComingSoon from "../StudentGoalsComingSoon";
import SurveysComingSoon from "../SurveysComingSoon";

const Home = lazyWithReload(() => import("../../pages/Admin/Home"));
const Onboarding = lazyWithReload(() => import("../../pages/Admin/Onboarding"));
const OnboardingSuccess = lazyWithReload(() => import("../../pages/Admin/OnboardingSuccess"));
const ClassCreate = lazyWithReload(() => import("../../pages/Admin/ClassCreate"));
const ClassEditPage = lazyWithReload(() => import("../../pages/Admin/ClassEditPage"));
const ClassDetailPage = lazyWithReload(() => import("../../pages/Admin/ClassDetailPage"));
const HelpSupport = lazyWithReload(() => import("../../pages/Admin/HelpSupport"));
const ProjectCreation = lazyWithReload(() => import("../../pages/Admin/ProjectCreation"));
const Schools = lazyWithReload(() => import("../../pages/Admin/Schools"));
const Subjects = lazyWithReload(() => import("../../pages/Admin/Subjects"));
const StandardsProgression = lazyWithReload(() => import("../../pages/Admin/StandardsProgression"));
const StudentProfiles = lazyWithReload(() => import("../../pages/Admin/StudentProfiles"));
const TeacherProfiles = lazyWithReload(() => import("../../pages/Admin/TeacherProfiles"));
const UserManagement = lazyWithReload(() => import("../../pages/Admin/UserManagement"));
const AdminGradeEntry = lazyWithReload(() => import("../../pages/Teacher/StandardGradeEntry"));
const AdminClearView = lazyWithReload(() => import("../../pages/Admin/AdminClearView"));
const RequestsPage = lazyWithReload(() => import("../../pages/Admin/Requests"));
const RightsRequestsPage = lazyWithReload(() => import("../../pages/Admin/RightsRequests"));
const StudentProgressPage = lazyWithReload(() => import("../../pages/StudentProgressPage"));
const AtHomeSupportPage = lazyWithReload(() => import("../../pages/AtHomeSupportPage"));
const DataPointsReport = lazyWithReload(() => import("../../pages/DataPointsReport"));
const ReportsPage = lazyWithReload(() => import("../../pages/ReportsPage"));
const TrainingResourcesPage = lazyWithReload(() => import("../../pages/TrainingResourcesPage"));
const AdminConnect = lazyWithReload(() => import("../../pages/Admin/AdminConnect"));
const OnboardingDocuments = lazyWithReload(() => import("../../pages/Admin/OnboardingDocuments"));

const allAdminLinks = [
  {
    to: "/admin/home",
    label: "Home",
    feature: "home",
    groupLabel: "User Management",
    children: [
      { to: "/admin/project-creation", label: "Roster Upload", feature: "rosters" },
      { to: "/admin/schools", label: "Schools", feature: "grading" },
      { to: "/admin/teacher-profiles", label: "Teachers", feature: "teacherProfiles" },
      { to: "/admin/student-profiles", label: "Students", feature: "studentProfiles" },
      { to: "/admin/user-management", label: "User Management", feature: "userManagement" },
      { to: "/admin/classes", label: "Classes", feature: "classes" },
      { to: "/admin/subjects", label: "Subjects", feature: "classes" },
      { to: "/admin/requests", label: "Review Requests", feature: "requests" },
      { to: "/admin/rights-requests", label: "Parent rights", feature: "requests" },
    ],
  },
  {
    to: "/admin/grading",
    label: "Grading",
    feature: "grading",
    children: [
      { to: "/admin/standards-progression", label: "Data Entry", feature: "dataEntry" },
      { to: "/admin/data-points-report", label: "View Data", feature: "viewData" },
      { to: "/admin/reports", label: "Reports", feature: "reports" },
    ],
  },
  {
    to: "/admin/action",
    label: "Action",
    feature: "action",
    children: [
      { to: "/admin/action/goals", label: "Student Goals (Coming soon)", feature: "goals" },
      { to: "/admin/action-cards", label: "Action Cards", feature: "actionCards" },
    ],
  },
  {
    to: "/admin/connect",
    label: "Connect",
    feature: "connect",
    children: [
      { to: "/admin/messaging", label: "Messaging", feature: "messaging" },
    ],
  },
  {
    to: "/admin/help-support",
    label: "Help",
    feature: "helpSupport",
    children: [
      { to: "/admin/help-support", label: "Help & Support", feature: "helpSupport" },
      { to: "/admin/training-resources", label: "Training Resources", feature: "helpSupport" },
      { to: "/admin/onboarding-documents", label: "Onboarding Documents", feature: "helpSupport" },
      { to: "/admin/surveys", label: "Surveys (Coming soon)", feature: "helpSupport" },
    ],
  },
];

const pageFallback = (
  <div style={{ padding: "2rem", textAlign: "center", color: "#6b7280" }}>
    Loading page...
  </div>
);

export default function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const userRoles = useMemo(() => user?.roles || user?.role || [], [user?.roles, user?.role]);
  const onboardingRequired = Boolean(user?.onboarding_required);
  const onOnboardingRoute =
    location.pathname.startsWith("/admin/onboarding");

  const adminLinks = useMemo(() => {
    if (onboardingRequired) {
      return [
        {
          to: "/admin/onboarding",
          label: "Complete setup",
          feature: "home",
        },
      ];
    }
    return filterLinksByPermissions(allAdminLinks, userRoles);
  }, [userRoles, onboardingRequired]);

  if (onboardingRequired && !onOnboardingRoute) {
    return <Navigate to="/admin/onboarding" replace />;
  }

  return (
    <div className="ae-shell">
      <Sidebar links={adminLinks} forceReloadOnNavigate />
      <div className="ae-shell-content">
        <Header />
        <main className="ae-shell-main">
          <Suspense fallback={pageFallback}>
            <Routes>
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="onboarding/success" element={<OnboardingSuccess />} />
              <Route path="home" element={<Home />} />
              <Route path="grading" element={<Navigate to="/admin/standards-progression" replace />} />
              <Route path="action" element={<Navigate to="/admin/action-cards" replace />} />
              <Route path="action/goals" element={<StudentGoalsComingSoon />} />
              <Route path="classes" element={<Home />} />
              <Route path="schools" element={<Schools />} />
              <Route path="subjects" element={<Subjects />} />
              <Route path="standard-progressions" element={<Navigate to="/admin/standards-progression" replace />} />
              <Route path="reports" element={<ReportsPage role="admin" />} />
              <Route path="action-cards" element={<RequestsPage />} />
              <Route path="messaging" element={<AdminConnect />} />
              <Route path="training-resources" element={<TrainingResourcesPage role="admin" />} />
              <Route path="connect" element={<Navigate to="/admin/messaging" replace />} />
              <Route path="class-edit" element={<ClassEditPage />} />
              <Route path="classes/create" element={<ClassCreate />} />
              <Route path="class/:classId" element={<ClassDetailPage />} />
              <Route path="classes/:classId" element={<ClassDetailPage />} />
              <Route path="help-support" element={<HelpSupport />} />
              <Route path="surveys" element={<SurveysComingSoon />} />
              <Route path="project-creation" element={<ProjectCreation />} />
              <Route path="onboarding-documents" element={<OnboardingDocuments />} />
              <Route path="standards-progression" element={<StandardsProgression />} />
              <Route path="professional-development" element={<TrainingResourcesPage role="admin" />} />
              <Route path="grade-entry" element={<AdminGradeEntry />} />
              <Route path="student-profiles" element={<StudentProfiles />} />
              <Route path="student-profiles/:studentId" element={<StudentProfiles />} />
              <Route path="student-progress/:studentId" element={<StudentProgressPage role="admin" />} />
              <Route path="student-progress/:studentId/:classId" element={<StudentProgressPage role="admin" />} />
              <Route path="student-progress-family/:studentId" element={<AdminClearView />} />
              <Route path="student-progress-family/:studentId/:classId" element={<AdminClearView />} />
              <Route path="at-home-support" element={<AtHomeSupportPage />} />
              <Route path="data-points-report" element={<DataPointsReport role="admin" />} />
              <Route path="teacher-profiles" element={<TeacherProfiles />} />
              <Route path="teacher-profiles/:teacherId" element={<TeacherProfiles />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="parent-invitations" element={<Navigate to="/admin/user-management" replace />} />
              <Route path="requests" element={<RequestsPage />} />
              <Route path="rights-requests" element={<RightsRequestsPage />} />
              <Route path="*" element={<Navigate to="home" />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}
